import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
	Autocomplete,
	AutocompleteChangeDetails,
	AutocompleteChangeReason,
	Box,
	FormHelperText,
	InputAdornment,
	Stack,
	TextField,
} from "@mui/material";
import styles from "common/components/FormTextField/FormField.module.css";
import { Adornment } from "common/utils/AdornmentUtils";
import React, { SyntheticEvent } from "react";
export type SimpleAutocompleteOption = {
	label: string;
	value?: any; // maybe instead extend mui's UseAutocompleteProps
	subtext?: string;
	selected?: boolean;
	id?: any;
	disabled?: boolean;
};

//extends AutocompleteProps ?
export interface ISimpleAutocompleteProps extends Omit<
	React.ComponentProps<typeof Autocomplete>,
	"renderInput"
> {
	options: SimpleAutocompleteOption[];
	label?: string;
	helperText?: string;
	helperTextColor?: "primary" | "secondary" | "warn" | "info" | "success";
	helperTextError?: string;
	value?: any;
	isError?: boolean;
	name?: string;
	placeholder?: string;
	adornment?: Adornment;
	showClearButton?: boolean;
	onChange: (
		event: SyntheticEvent<Element, Event>,
		value: SimpleAutocompleteOption,
		reason: AutocompleteChangeReason,
		details?: AutocompleteChangeDetails<string>
	) => void;
}

function SimpleAutocomplete({
	options,
	label,
	helperText,
	helperTextColor,
	helperTextError,
	value,
	isError,
	name,
	adornment,
	placeholder,
	multiple = false,
	showClearButton = false,
	onChange,
	...autoCompleteProps
}: ISimpleAutocompleteProps) {
	const inputId = `${name || "unknown"}`;
	return (
		<Autocomplete
			{...autoCompleteProps}
			multiple={multiple}
			options={options}
			autoHighlight
			disableClearable={value ? false : true}
			getOptionLabel={(option: SimpleAutocompleteOption) =>
				options && option.label ? option.label : ""
			}
			isOptionEqualToValue={(option, val) => option.label === val.label} // this and getOptionLabel are here just to deal with MUI console warnings
			getOptionDisabled={(option: SimpleAutocompleteOption) =>
				!!option?.disabled
			}
			defaultValue={multiple ? value || [] : value || null}
			value={multiple ? value || [] : value || null}
			sx={{
				"& .MuiOutlinedInput-root": placeholder
					? {
							py: "inherit",
							paddingRight: "30px !important", //Remove padding for invisible icon
						}
					: { py: "inherit", paddingRight: "4% !important" },
			}}
			onChange={onChange}
			renderOption={(props, option, { selected }) => (
				<li {...props} key={option.label}>
					{option.label}
					<Box
						component={CheckIcon}
						sx={{
							width: 18,
							height: 18,
							float: "right",
							visibility: selected ? "visible" : "hidden",
						}}
						key={multiple ? undefined : option.label}
					/>
				</li>
			)}
			ChipProps={{
				sx: {
					bgcolor: "white",
					border: "1px solid",
					borderColor: "var(--oc-palette-secondary-300)",
					borderRadius: "6px",
					fontSize: "14px",
					height: "24px",
				},
				deleteIcon: <CloseIcon />,
			}}
			renderInput={(params) => (
				<Stack>
					<label
						htmlFor={inputId}
						className={styles.label + (params.disabled ? " disabled" : "")}
					>
						{label}
					</label>
					<TextField
						{...params}
						name={inputId || null}
						error={isError}
						placeholder={placeholder ? placeholder : null}
						ref={params.InputProps.ref}
						InputProps={{
							...params.InputProps,
							startAdornment:
								adornment?.position === "start" ? (
									<>
										<InputAdornment position="start">
											{adornment.content}
										</InputAdornment>
										{params.InputProps.startAdornment}
									</>
								) : undefined,
							endAdornment:
								adornment?.position === "end" ? (
									<>
										<InputAdornment position="end" disablePointerEvents>
											{adornment.content}
										</InputAdornment>
										{params.InputProps.startAdornment}
									</>
								) : showClearButton ? (
									params.InputProps.endAdornment
								) : undefined,
						}}
						inputProps={{
							...params.inputProps,
							"data-testid": inputId,
							autoComplete: "off",
							id: inputId,
						}}
						sx={{
							height: "40px",
							"& .Mui-disabled": {
								color: "var(--oc-palette-text-disabled)",
							},
							"& .MuiOutlinedInput-root": {
								height: "40px",
								bgcolor: params.disabled
									? "var(--oc-palette-secondary-100)"
									: "",
								color: "var(--oc-palette-text-primary)",
								"& fieldset": {
									borderRadius: 1,
									border: "1px solid",
									borderColor: isError
										? "var(--oc-palette-error-200)"
										: "var(--oc-palette-secondary-200)",
									padding: "8px 12px",
								},
								"&.Mui-focused fieldset": {
									boxShadow: isError
										? "0px 0px 0px 4px var(--oc-palette-error-box-shadow)"
										: "0px 0px 0px 4px var(--oc-palette-primary-box-shadow)",
									borderColor: isError
										? "var(--oc-palette-error-200)"
										: "var(--oc-palette-primary-500)",
								},
							},
						}}
					/>
					{helperText && (
						<FormHelperText
							error={isError}
							className={
								styles.helperText + (isError ? " error" : " " + helperTextColor)
							}
							sx={{
								fontSize: "14px",
								marginTop: 1,
								maxWidth: "280px",
							}}
						>
							{isError && helperTextError ? helperTextError : helperText}
						</FormHelperText>
					)}
				</Stack>
			)}
		/>
	);
}

export default SimpleAutocomplete;
